// Realistic Snow Effect for Hero Winter Section
class HeroWinterSnowEffect extends HTMLElement {
  constructor() {
    super()
    this.canvas = null
    this.ctx = null
    this.snowWrapper = null
    this.snowflakes = []
    this.animationId = null
    this.maxSnowflakes = this.calculateMaxSnowflakes()
    this.isInitialized = false
    this.resizeHandler = null
    this.destroyHandler = null
  }

  calculateMaxSnowflakes() {
    const width = window.innerWidth
    if (width >= 1024) {
      return 100
    } else if (width >= 768) {
      return 50
    } else {
      return 25
    }
  }

  connectedCallback() {
    this.init()
  }

  disconnectedCallback() {
    this.destroy()
  }

  init() {
    this.canvas = document.getElementById('hero-winter-snow-canvas')
    this.snowWrapper = document.querySelector('.hero-winter-snow-wrapper')

    if (!this.canvas || !this.snowWrapper) {
      return
    }

    this.ctx = this.canvas.getContext('2d')
    this.setupCanvas()
    this.createSnowflakes()
    this.startAnimation()
    this.bindEvents()
    this.isInitialized = true
  }

  setupCanvas() {
    this.resizeCanvas()
  }

  resizeCanvas() {
    const rect = this.snowWrapper.getBoundingClientRect()
    this.canvas.width = rect.width
    this.canvas.height = rect.height

    // Recalculate and recreate snowflakes on resize
    const newMaxSnowflakes = this.calculateMaxSnowflakes()
    if (newMaxSnowflakes !== this.maxSnowflakes) {
      this.maxSnowflakes = newMaxSnowflakes
      this.createSnowflakes()
    }
  }

  createSnowflakes() {
    this.snowflakes = []
    for (let i = 0; i < this.maxSnowflakes; i++) {
      this.snowflakes.push(new Snowflake(this.canvas.width, this.canvas.height))
    }
  }

  startAnimation() {
    const animate = () => {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)

      // Update and draw snowflakes
      this.snowflakes.forEach(snowflake => {
        snowflake.update(this.canvas.width, this.canvas.height)
        snowflake.draw(this.ctx)
      })

      this.animationId = requestAnimationFrame(animate)
    }

    animate()
  }

  bindEvents() {
    this.resizeHandler = () => this.resizeCanvas()
    this.destroyHandler = () => this.destroy()

    window.addEventListener('resize', this.resizeHandler)
    window.addEventListener('beforeunload', this.destroyHandler)
  }

  destroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
    if (this.destroyHandler) {
      window.removeEventListener('beforeunload', this.destroyHandler)
    }
    this.isInitialized = false
  }
}

// Snowflake class for more realistic snow
class Snowflake {
  constructor(canvasWidth, canvasHeight) {
    this.x = Math.random() * canvasWidth
    this.y = Math.random() * canvasHeight - canvasHeight
    this.size = Math.random() * 4 + 2
    // this.speed = Math.random() * 3 + 1;
    this.speed = (Math.random() * 3 + 1) * (window.innerWidth > 767 ? 0.5 : 0.4)
    this.wind = Math.random() * 0.5 - 0.25
    this.rotation = Math.random() * Math.PI * 2
    this.rotationSpeed = Math.random() * 0.02 - 0.01
    this.opacity = Math.random() * 0.8 + 0.2
    this.branches = Math.floor(Math.random() * 3) + 4 // 4-6 branches
  }

  update(canvasWidth, canvasHeight) {
    this.y += this.speed
    this.x += this.wind
    this.rotation += this.rotationSpeed

    // Reset when off screen
    if (this.y > canvasHeight + 10) {
      this.y = -10
      this.x = Math.random() * canvasWidth
    }

    if (this.x > canvasWidth + 10) {
      this.x = -10
    } else if (this.x < -10) {
      this.x = canvasWidth + 10
    }
  }

  draw(ctx) {
    ctx.save()
    ctx.translate(this.x, this.y)
    ctx.rotate(this.rotation)
    ctx.globalAlpha = this.opacity
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)'
    ctx.lineWidth = 0.5

    // Draw snowflake shape
    this.drawSnowflake(this.size, ctx)

    ctx.restore()
  }

  drawSnowflake(size, ctx) {
    ctx.beginPath()

    // Center circle
    ctx.arc(0, 0, size * 0.3, 0, Math.PI * 2)
    ctx.fill()

    // Draw branches
    for (let i = 0; i < this.branches; i++) {
      const angle = ((Math.PI * 2) / this.branches) * i
      ctx.save()
      ctx.rotate(angle)

      // Main branch
      ctx.beginPath()
      ctx.moveTo(0, 0)
      ctx.lineTo(size, 0)
      ctx.stroke()

      // Side branches
      ctx.beginPath()
      ctx.moveTo(size * 0.3, 0)
      ctx.lineTo(size * 0.6, -size * 0.2)
      ctx.moveTo(size * 0.3, 0)
      ctx.lineTo(size * 0.6, size * 0.2)
      ctx.stroke()

      // Branch tips
      ctx.beginPath()
      ctx.arc(size, 0, size * 0.2, 0, Math.PI * 2)
      ctx.fill()

      ctx.restore()
    }
  }
}

// Register the custom element
customElements.define('hero-winter-snow', HeroWinterSnowEffect)
